/* eslint-disable camelcase */
import ExcelJS from 'exceljs';
import { pad } from '@rjsf/utils';
import { utcToSite } from './datetimeUtils';

const isItemDesignation = (itemDesignation) => {
  const pattern = /^([+=-])([a-zA-Z0-9]+[+-.=])*[a-zA-Z0-9]+$/;
  return pattern.test(itemDesignation);
};
class ParseExcelError extends Error {
  constructor(errors) {
    super('ParseExcelError');
    this.errors = errors;
  }
}

const buildMaintenanceMessageString = ({
  description,
  energized,
  operation,
  responsibility,
  noOfItems,
  eachItemDuration,
  noOfCrew,
  pc,
  pb,
  px,
  pv,
  pm,
  pe,
  pex,
  se,
  pg,
  avarageManHourPerYear,
  noOfMaintenananceDuringLifetime,
}) => {
  let updatedDescription;
  updatedDescription = `${description}\n\n`;
  updatedDescription += `Site energized: ${energized}. \n`;
  updatedDescription += operation ? `Operation: ${operation}. \n` : '';
  if (responsibility) {
    const responsibilityText = responsibility === 'C' ? 'Customer' : 'HE';
    updatedDescription += `Responsibility: ${responsibilityText}. \n`;
  }
  updatedDescription += `Number of items: ${noOfItems.toString()}. \n`;
  updatedDescription += `Duration per item: ${
    eachItemDuration.result ? eachItemDuration.result.toString() : eachItemDuration.toString()
  } hours \n`;
  updatedDescription += `Number of crew members needed: ${
    noOfCrew.result ? noOfCrew.result.toString() : noOfCrew.toString()
  }. \n`;
  updatedDescription += `Duration of maintenance / man hours: ${(
    (eachItemDuration.result ? eachItemDuration.result : eachItemDuration) *
    (noOfCrew.result ? noOfCrew.result : noOfCrew) *
    (noOfItems.result ? noOfItems.result : noOfItems)
  ).toString()} hours \n`;
  updatedDescription += `Roles needed: \n`;
  updatedDescription += pc ? `   Control specialist: ${pc.toString()}. \n` : '';
  updatedDescription += pb ? `   Breaker specialist: ${pb.toString()}. \n` : '';
  updatedDescription += px ? `   Transfomer specialist: ${px.toString()}. \n` : '';
  updatedDescription += pv ? `   Valve specialist: ${pv.toString()}. \n` : '';
  updatedDescription += pm ? `   Mechanic: ${pm.toString()}. \n` : '';
  updatedDescription += pe ? `   Electrician: ${pe.toString()}. \n` : '';
  updatedDescription += pex ? `   External Personel: ${pex.toString()}. \n` : '';
  updatedDescription += se ? `   Service Engineer: ${se.toString()}. \n` : '';
  updatedDescription += pg ? `   Helper: ${pg.toString()}. \n` : '';
  updatedDescription += '\n';
  if (avarageManHourPerYear) {
    const manHourPerYear = avarageManHourPerYear.result
      ? avarageManHourPerYear.result.toString()
      : avarageManHourPerYear.toString();
    updatedDescription += `Avarage man hour/year: ${manHourPerYear}. \n`;
  }
  updatedDescription += noOfMaintenananceDuringLifetime
    ? `Number of maintenance during lifetime: ${noOfMaintenananceDuringLifetime.result ? noOfMaintenananceDuringLifetime.result.toString() : noOfMaintenananceDuringLifetime.toString()}. \n`
    : '';

  return updatedDescription;
};

const addMissingDataToMaintenanceSeries = (
  maintenanceSeries,
  siteComponents,
  startDate,
  endDate,
  timezone
) => {
  let allComponentsFound = true;

  const maintenanceSeriesWithCorrectData = maintenanceSeries.eventSeries.map((series) => {
    const itemDesignationIds = [];
    series.itemDesignations.forEach((itemDesignation) => {
      const foundComp = siteComponents.find((c) => c.itemDesignation === itemDesignation);

      if (foundComp) {
        itemDesignationIds.push(foundComp.id);
      } else {
        allComponentsFound = false;
      }
    });

    let occurs = '';
    let interval;
    // Weeks
    if (series.periodicity > 0 && series.periodicity < 0.0833) {
      occurs = 'WEEKLY';
      interval = Math.round(series.periodicity * 52);
    }
    // Months
    else if (series.periodicity >= 0.0833 && series.periodicity < 1) {
      occurs = 'MONTHLY';
      interval = Math.round(series.periodicity * 12);
    }
    // Years
    else {
      occurs = 'YEARLY';
      interval = Math.round(series.periodicity);
    }

    // we need to include tz info and adjust dtstart to site time
    const siteStart = utcToSite(startDate || Date.now(), timezone);
    const time = `${pad(siteStart.getHours(), 2)}${pad(siteStart.getMinutes(), 2)}${pad(siteStart.getSeconds(), 2)}`;
    let recurrenceRule = `DTSTART;TZID=${timezone}:${siteStart.getFullYear()}${pad(
      siteStart.getMonth() + 1,
      2
    )}${pad(siteStart.getDate(), 2)}T${time}\nRRULE:INTERVAL=${interval};FREQ=${occurs}`;

    if (endDate) {
      const siteEnd = utcToSite(endDate, timezone);
      const timeEnd = `${pad(siteEnd.getHours(), 2)}${pad(siteEnd.getMinutes(), 2)}${pad(siteEnd.getSeconds(), 2)}`;
      recurrenceRule += `;UNTIL=${siteEnd.getFullYear()}${pad(
        siteEnd.getMonth() + 1,
        2
      )}${pad(siteEnd.getDate(), 2)}T${timeEnd}`;
    }

    // replacing components itemDesignations with the ids of the components
    return {
      ...series,
      components: itemDesignationIds,
      recurrenceRule,
      from: startDate || Date.now(),
      to: startDate || Date.now(),
      notificationDate: startDate,
    };
  });

  return [
    allComponentsFound,
    { ...maintenanceSeries, eventSeries: maintenanceSeriesWithCorrectData },
  ];
};

const normalizeAndAddRootToItemDesignation = (itemDesignation, rootItemDesignation) => {
  // Clean item designation from some potential junk
  let adjustedItemDesignation =
    itemDesignation && itemDesignation.indexOf(' ') > 0
      ? itemDesignation.substring(0, itemDesignation.indexOf(' '))
      : itemDesignation;

  if (isItemDesignation(rootItemDesignation) && isItemDesignation(adjustedItemDesignation)) {
    // Make the item designation absolute if root component is a real item designation
    adjustedItemDesignation = rootItemDesignation + itemDesignation;
  }

  return adjustedItemDesignation;
};

function cleanItemDesignationString(itemDesignationString) {
  // remove line breaks
  let newItemDesignationString = itemDesignationString.replace(/(\r\n|\n|\r)/gm, '');
  // remove spaces
  newItemDesignationString = newItemDesignationString.replace(/ /g, '');

  return newItemDesignationString;
}

function filterComponenstsUnique(components) {
  // only keep unique & remove components that already exist on site
  return components.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t.itemDesignation === value.itemDesignation)
  );
}

function countItemDesignations(components) {
  let count = 0;
  components.forEach((comp) => {
    if (isItemDesignation(comp.itemDesignation)) count += 1;
  });
  return count;
}

function createMappingsFromComponents(components) {
  // Will convert components with dbids to adapt to mapping format required by endpoint
  return components.map((comp) => ({
    itemDesignations: [comp.itemDesignation],
    dbIds: comp.dbIds,
    geometryIds: [],
    position: { x: 0, y: 0, z: 0 },
  }));
}

function addAttributesToComponents(components) {
  // Will convert simple properties on components to adapt to attribute format required by endpoint
  return components.map((comp) => {
    let attributes = [];

    if (comp.properties) {
      attributes = comp.properties.map((prop) => {
        let dataType = 'string';
        let { value } = prop;
        if (typeof value === 'number') {
          dataType = 'number';
        } else if (typeof value === 'boolean') {
          dataType = 'boolean';
        } else if (/^\d+$/.test(value)) {
          dataType = 'number';
          value = Number(value);
        } else if (value.toLowerCase() === 'true') {
          dataType = 'boolean';
          value = true;
        } else if (value.toLowerCase() === 'false') {
          dataType = 'boolean';
          value = false;
        } else if (typeof value === 'string' && value.trim().length === 0) {
          // Some attributes like ABB_PROJECT NAME has ' ' as value.
          // This breaks our validation in with RJSF since value is a required field that cannot be empty.
          // To resole this, we set ' ' to 'Not defined'.
          value = 'Not defined';
        }

        const attr = {
          name: prop.name,
          dataType,
          values: [{ value, timestamp: new Date(), quality: 1 }],
          itemDesignation: comp.itemDesignation,
          category: prop.category,
        };

        return attr;
      });
    }

    return { ...comp, attributes };
  });
}

function createAllLevelsOfComponents(components, existingComponents, rootComponent) {
  const newComponents = [];
  // Create all components including parents
  components.forEach((comp) => {
    if (isItemDesignation(comp.itemDesignation)) {
      const levelArray = comp.itemDesignation.split(/([+=\-.][^+=\-.]*)/g).filter((l) => l !== '');

      // Go through each level and create components
      levelArray.forEach((c, index) => {
        const prefix = c.charAt(0);
        let parentItemDesignation;
        let itemDesignation;
        if (index === 0) {
          parentItemDesignation = rootComponent.itemDesignation;
          itemDesignation = c;
        } else {
          parentItemDesignation = levelArray.slice(0, index).join('');
          itemDesignation = parentItemDesignation + c;
        }

        // As root item designation is validated as an item designation it is added to the complete item designation
        // Add all properties to the component to the last level
        if (index + 1 === levelArray.length) {
          newComponents.push({
            ...comp,
            itemDesignation,
            referenceDesignation: itemDesignation,
            name: comp.itemDesignation === itemDesignation && comp.name ? comp.name : c,
            parentItemDesignation,
            type: prefix === '=' ? 'group' : 'component',
          });
        } else {
          newComponents.push({
            itemDesignation,
            referenceDesignation: itemDesignation,
            name: comp.itemDesignation === itemDesignation && comp.name ? comp.name : c,
            parentItemDesignation,
            type: prefix === '=' ? 'group' : 'component',
          });
        }
      });
    } else {
      // If not an item designation just add it to the root as is
      newComponents.push({
        itemDesignation: comp.itemDesignation,
        referenceDesignation: comp.itemDesignation,
        name: comp.itemDesignation,
        parentItemDesignation: rootComponent.itemDesignation,
        type: 'component',
      });
    }
  });

  // Only keep unique & remove components that already exist on site
  return newComponents.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t.itemDesignation === value.itemDesignation) &&
      !existingComponents.some((c) => c.itemDesignation === value.itemDesignation)
  );
}

// Recursive resolve parenthesis in item designation
const resolveParenthesisItemDesignation = (itemDesignation) => {
  let itemDesignations = [];
  let errors = [];

  const indexOfLeftParanthesis = itemDesignation.indexOf('(');
  if (indexOfLeftParanthesis >= 0) {
    const indexOfRightParanthesis = itemDesignation.indexOf(')', indexOfLeftParanthesis + 1);
    if (indexOfRightParanthesis > 0) {
      const withinParenthesis = itemDesignation.substring(
        indexOfLeftParanthesis + 1,
        indexOfRightParanthesis
      );

      if (withinParenthesis.includes('(')) {
        errors.push(
          `A parenthesis within a parenthesis was found for reference designation: ${itemDesignation}. This is not allowed.`
        );
      } else {
        // Create array of substrings with comma as separator
        const itemDesignationLevel = withinParenthesis.split(',');
        if (itemDesignationLevel.length === 1) {
          errors.push(
            `At least one of the parenthesis does not have a comma for reference designation: ${itemDesignation}`
          );
        } else if (itemDesignationLevel.length > 1) {
          itemDesignationLevel.forEach((level) => {
            // Return variations of the item designation and call this function again to resolve if there are more paranthesis
            // building all the different variations recursively
            const [items, err] = resolveParenthesisItemDesignation(
              itemDesignation.substring(0, indexOfLeftParanthesis) +
                level +
                itemDesignation.substring(indexOfRightParanthesis + 1)
            );

            itemDesignations = [...itemDesignations, ...items];
            errors = [...errors, ...err];
          });
        } else {
          errors.push(
            `At least one of the parenthesis is empty for reference designation: ${itemDesignation}`
          );
        }
      }
    } else {
      errors.push(
        `There is no right parenthesis in reference designation matching left parenthesis for: ${itemDesignation}`
      );
    }
  } else {
    itemDesignations = [cleanItemDesignationString(itemDesignation)];
  }

  return [itemDesignations, errors];
};

const checkUnique = (array, key, errors, message, worksheetName) => {
  const uniqueVal = {};

  array.some((item, index) => {
    const value = item[key];
    const property = typeof value === 'string' ? value.trim() : value;
    // eslint-disable-next-line no-prototype-builtins
    if (uniqueVal.hasOwnProperty(property)) {
      // Index + 2 since the first row in the excel sheet is the header and index starts at 0.
      errors.push(`${worksheetName}: Row ${index + 2}: ${message}`);
      return true;
    }
    if (property) {
      uniqueVal[property] = true;
    }
    return false;
  });
};

export const cleanSheet = (sheet, skipSecondRow = false) => {
  const validRows = [];

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    // Skip the second row (e.g. used for notes rows in some templates[TFRClassificationConfigTemplate])
    if (skipSecondRow && rowNumber === 2) return;

    // Removes all empty rows, we can potentially have thousands of them.
    // Also processes rich text values appropriately.
    const rowData = row.values.map((value) => {
      const rawValue = value?.richText ? value.richText.map((part) => part.text).join('') : value;
      // Trim string values to avoid issues with accidental whitespace
      return typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    });

    const isValidRow = !rowData.every(
      (value) => value === null || value === '' || value === 'empty'
    );

    if (isValidRow) {
      rowData.shift();
      validRows.push(rowData);
    }
  });

  return validRows;
};

export const validateSheetHeaders = (headers, sheet, map, errorArray) => {
  const columnNames = Object.keys(map);

  headers.forEach((r, i) => {
    const rowName = r.replace(/\*/g, '').trim();
    const columnLetter = sheet.getColumn(i + 1).letter;

    if (!columnNames.includes(rowName) || (columnNames[i] && map[rowName] !== i)) {
      errorArray.push(`Column ${columnNames[i]} should be on column ${columnLetter}`);
    }
  });
};

const parseComponentSheet = async ({ workbook }) => {
  const componentSheet = workbook.worksheets.find((s) => s.name === 'Components');
  const parsedComponents = [];
  let validItemDesignations;
  const errors = [];

  if (!componentSheet) {
    return [[], [], ['No Components sheet found']];
  }

  const COMPONENT_SHEET_MAP = {
    'Reference Designation': 0,
    'Parent Reference Designation': 1,
    'Component Name': 2,
    'Component Type': 3,
    'Custom Id': 4,
  };

  const [sheetHeaders, ...validRows] = cleanSheet(componentSheet);

  validateSheetHeaders(sheetHeaders, componentSheet, COMPONENT_SHEET_MAP, errors);

  checkUnique(
    validRows,
    COMPONENT_SHEET_MAP['Reference Designation'],
    errors,
    'Reference Designation has to be unique.',
    'Components sheet'
  );

  if (!errors.length) {
    validItemDesignations = new Set(
      validRows.map((row) => row[COMPONENT_SHEET_MAP['Reference Designation']])
    );

    let rowValid = true;
    let foundSiteComponent = false;

    validRows.forEach((row, index) => {
      const name = row[COMPONENT_SHEET_MAP['Component Name']];
      const type = row[COMPONENT_SHEET_MAP['Component Type']];
      const customId = row[COMPONENT_SHEET_MAP['Custom Id']];
      const referenceDesignation = row[COMPONENT_SHEET_MAP['Reference Designation']];

      const parentReferenceDesignation =
        row[COMPONENT_SHEET_MAP['Parent Reference Designation']] || null;

      if (type === 'site') {
        if (foundSiteComponent) {
          errors.push(
            `Components sheet (row ${index + 2}): Only one component of type "site" is allowed.`
          );
        }
        if (parentReferenceDesignation?.trim()) {
          errors.push(
            `Components sheet (row ${index + 2}): A component of type "site" must not have a Parent Reference Designation.`
          );
        }
        foundSiteComponent = true;
      }

      if (!referenceDesignation) {
        rowValid = false;
        errors.push(`Components sheet (row ${index + 2}): Reference Designation is required.`);
      }

      if (index >= 1 && !parentReferenceDesignation && type !== 'site') {
        rowValid = false;
        errors.push(
          `Components sheet (row ${index + 2}): Parent Reference Designation is required.`
        );
      }

      if (
        index > 1 &&
        !validItemDesignations.has(parentReferenceDesignation) &&
        parentReferenceDesignation
      ) {
        rowValid = false;
        errors.push(
          `Components sheet (row ${index + 2}): The Parent Reference Designation does not match any Reference Designation.`
        );
      }

      if (index > 1 && parentReferenceDesignation === referenceDesignation) {
        rowValid = false;
        errors.push(
          `Components sheet (row ${index + 2}): The Reference Designation and Parent Reference Designation cannot be identical.`
        );
      }

      if (!name) {
        rowValid = false;
        errors.push(`Components sheet (row ${index + 2}): Component Name is required.`);
      }

      if (!type) {
        rowValid = false;
        errors.push(`Components sheet (row ${index + 2}): Component Type is required.`);
      }

      // Check that parent does not exist after in the list to avoid circualar dependencies when updating components
      if (
        parentReferenceDesignation != null &&
        validRows
          .slice(index + 1)
          .some(
            (vRow) =>
              parentReferenceDesignation === vRow[COMPONENT_SHEET_MAP['Reference Designation']]
          )
      ) {
        rowValid = false;
        errors.push(
          `Components sheet (row ${index + 2}): The parent reference designation appears after the component in the list.`
        );
      }

      const getCompType = (componentType) => {
        if (componentType === 'equipment') {
          return 'component';
        }

        // We keep this in case a user would attempt to use an older excel where virtual was set to area.
        if (componentType === 'area') {
          return 'virtual';
        }
        return componentType;
      };

      if (rowValid) {
        const parsedComponent = {
          itemDesignation: referenceDesignation,
          referenceDesignation,
          parentItemDesignation: parentReferenceDesignation,
          name,
          type: getCompType(type),
          customId: customId ? String(customId) : null,
        };
        parsedComponents.push(parsedComponent);
      }
    });
  }

  return [parsedComponents, validItemDesignations, errors];
};

const parseDocumentSheet = async ({ workbook, drawings, itemDesignations }) => {
  const documentSheet = workbook.worksheets.find((s) => s.name === 'Documents');
  const errors = [];
  const parsedDocuments = [];

  if (!documentSheet) {
    return [[], ['No Document sheet found']];
  }

  const DOCUMENT_SHEET_MAP = {
    Document: 0,
    'Reference Designation': 1,
    Width: 2,
    Height: 3,
  };

  // Removing the headers on the first row in the excel
  const [sheetHeaders, ...validRows] = cleanSheet(documentSheet);
  validateSheetHeaders(sheetHeaders, documentSheet, DOCUMENT_SHEET_MAP, errors);

  checkUnique(
    validRows,
    DOCUMENT_SHEET_MAP.Document,
    errors,
    'Document has to be unique.',
    'Documents sheet'
  );

  if (!errors.length) {
    let rowValid = true;

    validRows.forEach((row, index) => {
      const document = row[DOCUMENT_SHEET_MAP.Document];
      const referenceDesignation = row[DOCUMENT_SHEET_MAP['Reference Designation']];

      const height = row[DOCUMENT_SHEET_MAP.Height];
      const width = row[DOCUMENT_SHEET_MAP.Width];

      const drawingsMatched = drawings.filter((drawing) => drawing.originalName === document);

      if (drawingsMatched.length === 0) {
        rowValid = false;
        errors.push(
          `Documents Sheet: This document on row ${index + 2} does not exist on the site.`
        );
      }

      if (drawingsMatched.length > 1) {
        rowValid = false;
        errors.push(
          `Documents Sheet: There are several documents with the same document name that exist for row ${index + 2}. There can only be one, so make sure to remove the others and try again`
        );
      }

      if (!itemDesignations.has(referenceDesignation)) {
        rowValid = false;
        errors.push(
          `Documents Sheet: Reference designation on row ${index + 2} is was not included in the components sheet.`
        );
      }

      if (!referenceDesignation) {
        rowValid = false;
        errors.push(`Documents Sheet: Reference designation on row ${index + 2} is required.`);
      }

      if (!height) {
        rowValid = false;
        errors.push(`Documents Sheet: Height on row ${index + 2} is required.`);
      }

      if (!width) {
        rowValid = false;
        errors.push(`Documents Sheet: Width on row ${index + 2} is required.`);
      }

      if (rowValid) {
        const parsedDocument = {
          name: document,
          versionId: drawingsMatched[0].versionId,
          modelId: drawingsMatched[0].modelId,
          displayName: drawingsMatched[0].displayName,
          itemDesignation: referenceDesignation,
          height: Number(height),
          width: Number(width),
        };
        parsedDocuments.push(parsedDocument);
      }
    });
  }
  return [parsedDocuments, errors];
};

// Waiting for more info about this from Ben & Balaji about the structure.
const parseAttributesSheet = async ({ workbook, parsedComponents }) => {
  const parsedAttributes = {};
  const uniqueAttributes = new Set();
  const errors = [];

  const attributesSheet = workbook.worksheets.find((s) => s.name === 'Attributes');

  if (!attributesSheet) {
    return [[], ['No Attributes sheet found']];
  }

  const ATTRIBUTES_SHEET_MAP = {
    'Reference Designation': 0,
    'Attribute Name': 1,
    Value: 2,
    'Data type': 3,
    Category: 4,
    Unit: 5,
  };

  const [sheetHeaders, ...validRows] = cleanSheet(attributesSheet);
  validateSheetHeaders(sheetHeaders, attributesSheet, ATTRIBUTES_SHEET_MAP, errors);

  if (!errors.length) {
    let rowValid = true;

    validRows.forEach((row, index) => {
      const referenceDesignation = row[ATTRIBUTES_SHEET_MAP['Reference Designation']];

      const attributeName = row[ATTRIBUTES_SHEET_MAP['Attribute Name']];
      const value = row[ATTRIBUTES_SHEET_MAP.Value];
      const dataType = row[ATTRIBUTES_SHEET_MAP['Data type']];
      const category = row[ATTRIBUTES_SHEET_MAP.Category];
      const unit = row[ATTRIBUTES_SHEET_MAP.Unit];

      if (!referenceDesignation) {
        rowValid = false;
        errors.push(`Attributes sheet: Reference designation on row ${index + 2} is required.`);
      }

      if (!attributeName) {
        rowValid = false;
        errors.push(`Attributes sheet: Attribute name on row ${index + 2} is required.`);
      }

      if (value == null) {
        rowValid = false;
        errors.push(`Attributes sheet: Value on row ${index + 2} is required.`);
      }

      if (
        !dataType &&
        !(dataType === 'string' || dataType === 'boolean' || dataType === 'number')
      ) {
        rowValid = false;
        errors.push(
          `Attributes sheet: Data type on row ${index + 2} is required and needs to be one of string, boolean or number.`
        );
      }

      if (!parsedComponents.map((c) => c.itemDesignation).includes(referenceDesignation)) {
        rowValid = false;
        errors.push(
          `Attributes sheet: (Row ${index + 2}) ${referenceDesignation ? `No component with reference designation ${referenceDesignation}.` : 'Reference designation is empty.'}`
        );
      }

      if (rowValid) {
        const uniqueKey = `${attributeName}${referenceDesignation}`;

        if (!uniqueAttributes.has(uniqueKey)) {
          uniqueAttributes.add(uniqueKey);

          const parsedAttribute = {
            name: attributeName,
            dataType,
            values: [{ value, timestamp: new Date(), quality: 1 }],
            itemDesignation: referenceDesignation,
            category: category ? String(category) : null,
            ...(unit && unit !== '' && { unit }),
          };
          parsedAttributes[referenceDesignation] = [
            ...(parsedAttributes[referenceDesignation] || []),
            parsedAttribute,
          ];
        } else {
          errors.push(`Attributes sheet: Data on row ${index + 2} is duplicate, please delete it.`);
        }
      }
    });
  }

  return [
    parsedComponents.map((c) => ({
      ...c,
      attributes: parsedAttributes[c.itemDesignation] || [],
    })),
    errors,
  ];
};

const parseShapesSheet = async ({ workbook, documents, itemDesignations }) => {
  const documentsWithShapes = {};
  const errors = [];

  const shapesSheet = workbook.worksheets.find((s) => s.name === 'Shapes');

  if (!shapesSheet) {
    return [[], ['No Attributes sheet found']];
  }

  const SHAPES_SHEET_MAP = {
    Document: 0,
    'Reference Designation': 1,
    'Shape Name': 2,
    X1: 3,
    Y1: 4,
    X2: 5,
    Y2: 6,
    Width: 7,
    Height: 8,
  };

  const [sheetHeaders, ...validRows] = cleanSheet(shapesSheet);
  validateSheetHeaders(sheetHeaders, shapesSheet, SHAPES_SHEET_MAP, errors);

  if (!errors.length) {
    let rowValid = true;

    validRows.forEach((row, index) => {
      const documentName = row[SHAPES_SHEET_MAP.Document];

      const referenceDesignation = row[SHAPES_SHEET_MAP['Reference Designation']];

      const shapeName = row[SHAPES_SHEET_MAP['Shape Name']];
      const x1 = row[SHAPES_SHEET_MAP.X1];
      const y1 = row[SHAPES_SHEET_MAP.Y1];
      const x2 = row[SHAPES_SHEET_MAP.X2];
      const y2 = row[SHAPES_SHEET_MAP.Y2];
      const width = row[SHAPES_SHEET_MAP.Width];
      const height = row[SHAPES_SHEET_MAP.Height];

      if (
        !documentName ||
        !referenceDesignation ||
        !shapeName ||
        !x1 ||
        !y1 ||
        !x2 ||
        !y2 ||
        !width ||
        !height
      ) {
        rowValid = false;
        errors.push(
          `Shapes sheet: At least one column is not entered on row ${index + 2}. All values are required`
        );
      }

      if (!documents.some((d) => d.name === documentName)) {
        rowValid = false;
        errors.push(
          `Shapes sheet: Document on row ${index + 2} is not included in the documents sheet.`
        );
      }

      if (!itemDesignations.has(referenceDesignation)) {
        rowValid = false;
        errors.push(
          `Shapes sheet: Reference designation on row ${index + 2} is was not included in the components sheet.`
        );
      }

      if (rowValid) {
        const document = documents.find((d) => d.name === documentName);

        // The scalefactor transforms the coordinate system of visio to IdentiQ coordinate system
        // The number 250 and 88 comes from that the specific format of the .dwg always creates the drawing in the same size in THREE.js coordinates
        const scaleFactor = 250 / document.width;
        const offsetY = 88;

        // The divided by two etc is because in IdentiQ we use the center of the drawing as origa while Visio use bottom left corner as origo
        const geometry = {
          name: referenceDesignation,
          position: {
            x: x1 * scaleFactor + (width * scaleFactor) / 2,
            y: y1 * scaleFactor + offsetY - (height * scaleFactor) / 2,
            z: 0,
          },
          quaternion: {
            x: 0,
            y: 0,
            z: 0,
            w: 1,
          },
          scale: {
            x: width * scaleFactor,
            y: height * scaleFactor,
            z: 1,
          },
          geometryType: 'PlaneGeometry',
          material: {
            color: '#9898FC',
            opacity: 0.02,
            transparent: true,
          },
          type: 'object',
        };

        documentsWithShapes[documentName] = [
          ...(documentsWithShapes[documentName] || []),
          geometry,
        ];
      }
    });

    const documentsWithGeometries = documents.map((c) => ({
      ...c,
      geometries: documentsWithShapes[c.name] || [],
    }));

    // Sort by size of geometries
    documentsWithGeometries.forEach((doc) => {
      doc.geometries.sort(
        (mappingA, mappingB) =>
          mappingB.scale.x * mappingB.scale.y - mappingA.scale.x * mappingA.scale.y
      );

      // Make objects clickable in correct order by changing z in accordance with size
      /* eslint-disable no-param-reassign */
      doc.geometries.forEach((geometry, index) => {
        geometry.position.z = index;
      });
      /* eslint-enable no-param-reassign */
    });

    return [documentsWithGeometries, errors];
  }
  return [[], errors];
};

const getMaintenanceSheetMap = (hvdcMaintenancePlan) => ({
  itemDesignations: 0,
  itemName: 1,
  inspectionIdentifier: hvdcMaintenancePlan ? 2 : 3,
  noOfItems: hvdcMaintenancePlan ? 3 : 4,
  tBM: hvdcMaintenancePlan ? 4 : 5,
  noOfMaintenananceDuringLifetime: hvdcMaintenancePlan ? 5 : null,
  pc: 6,
  pb: 7,
  px: 8,
  pv: hvdcMaintenancePlan ? 9 : null,
  pm: 10,
  pe: 11,
  pg: 12,
  pex: hvdcMaintenancePlan ? 13 : null,
  noOfCrew: hvdcMaintenancePlan ? 14 : 13,
  eachItemDuration: hvdcMaintenancePlan ? 15 : 14,
  avarageManHourPerYear: hvdcMaintenancePlan ? 16 : 15,
  energized: 17,
  operation: hvdcMaintenancePlan ? 18 : null,
  responsibility: hvdcMaintenancePlan ? null : 2,
  se: hvdcMaintenancePlan ? null : 9,
  description: hvdcMaintenancePlan ? 19 : 18,
});

const mapItemDesignationsToNames = (
  itemDesginationsArray,
  rootComponent,
  itemName,
  absoluteItemDesignation
) =>
  itemDesginationsArray.map((item) => {
    const itemDesignationNotRoot = item !== rootComponent.itemDesignation;
    const absoluteDesignationForThisItem =
      absoluteItemDesignation && isItemDesignation(item) && itemDesignationNotRoot;
    return {
      name: itemName,
      itemDesignation: absoluteDesignationForThisItem ? rootComponent.itemDesignation + item : item,
    };
  });

const parseMaintenancePlanSheet = async ({
  workbook,
  components,
  site,
  org,
  startDate,
  endDate,
  timezone,
}) => {
  const parsedMaintenanceSeries = [];
  let maintenanceComponents = [];
  let missingComponents = [];
  const errors = [];
  let absoluteItemDesignation = true;

  const rootComponent = components.find((c) => c.parent == null);
  if (!rootComponent) {
    return [[], [], [], ['No root component on site, please add one and try again']];
  }
  if (!isItemDesignation(rootComponent.itemDesignation)) {
    // if top level component is not a real item designation we assume that we won't use an absolute item designation (include site level in the item designation string)
    absoluteItemDesignation = false;
  }

  // Only support single site
  const sites = components.filter((c) => c.type === 'site');
  if (sites.length > 1) {
    return [[], [], [], ['The import does not support multiple sites']];
  }

  const maintenanceSheet = workbook.worksheets.find((s) => s.name === 'Maintenance');

  if (!maintenanceSheet) {
    return [[], [], [], ['No Maintenace sheet found']];
  }

  let isHVDC = true;
  // Check if FACTS or HVDC template
  const maintenanceInspectionClass = maintenanceSheet.getRow(1).getCell(4).value;
  if (maintenanceInspectionClass === 'Maintenance Inspection Class') {
    isHVDC = false;
  }

  let rows = [];
  maintenanceSheet.eachRow({}, (row) => {
    const rowData = row.values;
    rowData.shift();
    rows.push(rowData);
  });
  rows = rows.slice(isHVDC ? 6 : 1);

  const MAINTENANCE_SHEET_MAP = getMaintenanceSheetMap(isHVDC);

  if (!errors.length) {
    const validateRow = (row, index) => {
      const requiredFields = [
        MAINTENANCE_SHEET_MAP.inspectionIdentifier,
        MAINTENANCE_SHEET_MAP.noOfItems,
        MAINTENANCE_SHEET_MAP.tBM,
        MAINTENANCE_SHEET_MAP.noOfCrew,
        MAINTENANCE_SHEET_MAP.eachItemDuration,
        MAINTENANCE_SHEET_MAP.description,
        MAINTENANCE_SHEET_MAP.energized,
      ];

      const missingFields = requiredFields.some((field) => !row[field]);
      if (missingFields) {
        errors.push(
          `Maintenance sheet: At least one column is not entered correctly on row ${
            index + (isHVDC ? 7 : 2)
          }.`
        );
        return false;
      }

      if (row[MAINTENANCE_SHEET_MAP.tBM] < 0) {
        errors.push(`Maintenance sheet: TBM is negative on row ${index + (isHVDC ? 7 : 2)}.`);
        return false;
      }

      if (typeof row[MAINTENANCE_SHEET_MAP.tBM] !== 'number') {
        errors.push(
          `Maintenance sheet: TBM is not a number on row ${index + (isHVDC ? 7 : 2)}. Make sure merged columns are either removed or split`
        );
        return false;
      }

      return true;
    };

    const processRow = (row, index) => {
      const itemDesignations = row[MAINTENANCE_SHEET_MAP.itemDesignations];
      const itemName = row[MAINTENANCE_SHEET_MAP.itemName];
      if (!itemDesignations || !itemName) return;

      if (!validateRow(row, index)) return;

      const [itemDesginationsArray, paranthesisErrors] =
        resolveParenthesisItemDesignation(itemDesignations);

      const itemDesignationsWithNames = mapItemDesignationsToNames(
        itemDesginationsArray,
        rootComponent,
        itemName,
        absoluteItemDesignation
      );

      maintenanceComponents = [...maintenanceComponents, ...itemDesignationsWithNames];

      if (paranthesisErrors.length === 0) {
        const description = buildMaintenanceMessageString({
          description: row[MAINTENANCE_SHEET_MAP.description],
          energized: row[MAINTENANCE_SHEET_MAP.energized],
          operation: row[MAINTENANCE_SHEET_MAP.operation],
          responsibility: row[MAINTENANCE_SHEET_MAP.responsibility],
          noOfItems: row[MAINTENANCE_SHEET_MAP.noOfItems],
          eachItemDuration: row[MAINTENANCE_SHEET_MAP.eachItemDuration],
          noOfCrew: row[MAINTENANCE_SHEET_MAP.noOfCrew],
          pc: row[MAINTENANCE_SHEET_MAP.pc],
          pb: row[MAINTENANCE_SHEET_MAP.pb],
          px: row[MAINTENANCE_SHEET_MAP.px],
          pv: row[MAINTENANCE_SHEET_MAP.pv],
          pm: row[MAINTENANCE_SHEET_MAP.pm],
          pe: row[MAINTENANCE_SHEET_MAP.pe],
          pex: row[MAINTENANCE_SHEET_MAP.pex],
          se: row[MAINTENANCE_SHEET_MAP.se],
          pg: row[MAINTENANCE_SHEET_MAP.pg],
          avarageManHourPerYear: row[MAINTENANCE_SHEET_MAP.avarageManHourPerYear],
          noOfMaintenananceDuringLifetime:
            row[MAINTENANCE_SHEET_MAP.noOfMaintenananceDuringLifetime],
        });

        const maintenanceSeries = {
          type: 'maintenance',
          planned: true,
          state: 'pending',
          assignedTo: 'NA',
          name: `Preventative maintenance: ${itemName} ${isHVDC ? 'RS NO' : 'Class'}: ${row[MAINTENANCE_SHEET_MAP.inspectionIdentifier]}`,
          description,
          itemDesignations: itemDesignationsWithNames.map((item) => item.itemDesignation),
          periodicity: row[MAINTENANCE_SHEET_MAP.tBM],
          notifyAt: '1_week',
          executionFrom: null,
          executionTo: null,
          attributes: {
            maintenanceType: 'Maintenance Type 1',
          },
          task: true,
          site,
          org,
          tags: [],
          files: [],
          events: {},
        };
        parsedMaintenanceSeries.push(maintenanceSeries);
      } else {
        errors.push(...paranthesisErrors);
      }
    };

    rows.forEach(processRow);

    // Only keep unique
    maintenanceComponents = filterComponenstsUnique(maintenanceComponents);

    // Filter away components that already exist to not create all levels without needing to
    missingComponents = maintenanceComponents.filter(
      (mc) => !components.some((c) => c.itemDesignation === mc.itemDesignation)
    );
    maintenanceComponents = createAllLevelsOfComponents(
      missingComponents,
      components,
      rootComponent
    );
  }

  let maintenanceSeriesData = {
    site,
    org,
    overwriteTag: 'HE MAL',
    eventSeries: parsedMaintenanceSeries,
  };

  // If startDate is not present, it will be added later once the startDate has been entered by the user
  if (startDate) {
    maintenanceSeriesData = addMissingDataToMaintenanceSeries(
      maintenanceSeriesData,
      components,
      startDate,
      endDate || null,
      timezone
    );
  }

  return [maintenanceSeriesData, maintenanceComponents, missingComponents, errors];
};

const parseMaintenanceExcel = async ({ file, components, site, org }) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);

  const [maintenanceSeries, maintenanceComponents, missingComponents, maintenanceErrors] =
    await parseMaintenancePlanSheet({
      workbook,
      components,
      site,
      org,
    });

  return [maintenanceSeries, maintenanceComponents, missingComponents, maintenanceErrors];
};

const parseAutomationExcel = async ({ file, drawings }) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);

  const documentsSheet = workbook.worksheets.find((s) => s.name === 'Documents');

  try {
    const [parsedComponents, itemDesignations, componentErrors] = await parseComponentSheet({
      workbook,
    });
    if (componentErrors.length > 0) {
      throw new ParseExcelError(componentErrors);
    }

    const [parsedComponentsWithAttributes, attributesErrors] = await parseAttributesSheet({
      workbook,
      parsedComponents,
    });

    if (attributesErrors.length > 0) {
      throw new ParseExcelError(attributesErrors);
    }

    // Only try parse documents and shapes if there is a documents sheet
    if (documentsSheet) {
      const [documents, documentErrors] = await parseDocumentSheet({
        workbook,
        drawings,
        itemDesignations,
      });

      if (documentErrors.length > 0) {
        throw new ParseExcelError(documentErrors);
      }

      const [documentsWithShapes, shapesErrors] = await parseShapesSheet({
        workbook,
        documents,
        itemDesignations,
      });

      if (shapesErrors.length > 0) {
        throw new ParseExcelError(shapesErrors);
      }

      return [parsedComponentsWithAttributes, documentsWithShapes, []];
    }
    return [parsedComponentsWithAttributes, [], []];
  } catch (err) {
    return [
      [],
      [],
      err instanceof ParseExcelError ? err.errors : ['An error occured parsing the excel'],
    ];
  }
};

const parseTFRClassificationConfigTemplate = async (signalConfig) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(signalConfig);

  const signalSheet = workbook.worksheets.find((s) => s.name === 'Signal Configuration');
  const errors = [];

  if (!signalSheet) {
    errors.push('No Signal Configuration sheet found');
    return { errors };
  }

  const CONFIG_SHEET_MAP = {
    'Converter Technology': 0,
    'Valve Deblock': 1,
    'AC Nom Volt RMS': 2,
    Computers: 3,
    'AC Breakers': 4,
    'AC Voltages': 5,
  };

  const [sheetHeaders, ...validRows] = cleanSheet(signalSheet, true);

  if (!validRows || validRows.length === 0) {
    errors.push('Signal Configuration sheet was empty');
    return { errors };
  }

  validateSheetHeaders(sheetHeaders, signalSheet, CONFIG_SHEET_MAP, errors);

  if (errors.length > 0) {
    return { errors };
  }

  const parsedConfig = {
    converter_technology: '',
    valve_deblock: '',
    ac_nom_volt_rms: null,
    computers: [],
    ac_breakers: [],
    ac_voltages: [],
  };

  const requiredFields = [
    { key: 'Converter Technology', configKey: 'converter_technology' },
    { key: 'Valve Deblock', configKey: 'valve_deblock' },
    { key: 'AC Nom Volt RMS', configKey: 'ac_nom_volt_rms', isNumber: true },
    { key: 'Computers', configKey: 'computers', isArray: true },
    { key: 'AC Breakers', configKey: 'ac_breakers', isArray: true },
    { key: 'AC Voltages', configKey: 'ac_voltages', isArray: true },
  ];

  const [...dataRows] = validRows;
  let rowValid = true;

  dataRows.forEach((row, index) => {
    if (index === 0) {
      requiredFields.forEach((field) => {
        const value = row[CONFIG_SHEET_MAP[field.key]];
        if (!value) {
          rowValid = false;
          errors.push(
            `Missing "${field.key}" value in the first row of the Signal Configuration sheet`
          );
        }
      });
    }
    if (rowValid) {
      requiredFields.forEach((field) => {
        const value = row[CONFIG_SHEET_MAP[field.key]];
        if (value) {
          if (field.isNumber) {
            if (typeof value !== 'number') {
              errors.push(`"${field.key}" value in the Signal Configuration sheet is not a number`);
              rowValid = false;
            } else {
              parsedConfig[field.configKey] = Number(value);
            }
          } else if (field.isArray) {
            parsedConfig[field.configKey].push(String(value));
          } else {
            parsedConfig[field.configKey] = String(value);
          }
        }
      });
    }
  });

  return rowValid ? { fileName: signalConfig.name, parsedConfig, errors } : { errors };
};

export {
  parseComponentSheet,
  parseDocumentSheet,
  parseShapesSheet,
  parseAttributesSheet,
  parseAutomationExcel,
  parseMaintenancePlanSheet,
  parseMaintenanceExcel,
  addMissingDataToMaintenanceSeries,
  filterComponenstsUnique,
  createAllLevelsOfComponents,
  countItemDesignations,
  addAttributesToComponents,
  createMappingsFromComponents,
  normalizeAndAddRootToItemDesignation,
  parseTFRClassificationConfigTemplate,
};
